import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FaBug, FaCommentDots, FaPaperPlane, FaRegLightbulb, FaRegStar } from "react-icons/fa";

const INTENTS = [
  { value: "idea", label: "Idea", icon: FaRegLightbulb, colorScheme: "purple" },
  { value: "issue", label: "Issue", icon: FaBug, colorScheme: "red" },
  { value: "praise", label: "Praise", icon: FaRegStar, colorScheme: "yellow" },
];

export default function FeedbackWidget() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [intent, setIntent] = useState("idea");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = message.trim().length >= 8;

  const intentMeta = useMemo(
    () => INTENTS.find((i) => i.value === intent) || INTENTS[0],
    [intent]
  );

  const onSubmit = async (evt) => {
    evt.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 350));
    setSubmitting(false);
    onClose();
    setMessage("");
    setName("");
    setEmail("");
    toast({
      title: "Feedback sent",
      description: "Thanks for sharing! We read every note.",
      status: "success",
      duration: 4000,
      isClosable: true,
    });
  };

  const accent = useColorModeValue("rgba(59,130,246,0.14)", "rgba(59,130,246,0.24)");
  const border = useColorModeValue("gray.100", "gray.700");
  const surface = useColorModeValue("white", "gray.900");

  return (
    <>
      <Tooltip label="Send feedback" placement="left">
        <IconButton
          aria-label="Open feedback form"
          icon={<FaCommentDots />}
          colorScheme="blue"
          size="lg"
          onClick={onOpen}
          rounded="full"
          position="fixed"
          bottom={{ base: 4, md: 6 }}
          right={{ base: 4, md: 6 }}
          zIndex={60}
          shadow="lg"
          _hover={{ transform: "translateY(-1px)", shadow: "xl" }}
          _active={{ transform: "translateY(0)" }}
        />
      </Tooltip>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent
          bg={surface}
          borderLeftWidth="1px"
          borderColor={border}
          shadow="2xl"
        >
          <DrawerCloseButton />
          <DrawerHeader
            bgGradient="linear(to-r, blue.600, purple.500)"
            color="white"
            pb={6}
          >
            <Text fontSize="sm" opacity={0.9}>
              We love hearing from you
            </Text>
            <Text fontSize="2xl" fontWeight="bold">
              Share your feedback
            </Text>
            <HStack spacing={2} mt={3}>
              <Badge colorScheme={intentMeta.colorScheme} variant="solid" px={2} py={0.5} borderRadius="md">
                {intentMeta.label}
              </Badge>
              <Text fontSize="sm" color="whiteAlpha.800">
                Choose a type below
              </Text>
            </HStack>
          </DrawerHeader>

          <DrawerBody px={6} pt={6} pb={0}>
            <Box
              borderWidth="1px"
              borderColor={border}
              borderRadius="lg"
              p={4}
              bg={accent}
            >
              <Text fontWeight={600} fontSize="md" mb={1}>
                What should we know?
              </Text>
              <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                Tell us about bugs, ideas, or things that make your workflow smoother.
                Screenshots, repro steps, and context help a ton.
              </Text>
            </Box>

            <Stack as="form" spacing={5} mt={6} onSubmit={onSubmit}>
              <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
                {INTENTS.map(({ value, label, icon: Icon, colorScheme }) => (
                  <Button
                    key={value}
                    variant={intent === value ? "solid" : "outline"}
                    colorScheme={colorScheme === "yellow" ? "yellow" : colorScheme}
                    onClick={() => setIntent(value)}
                    leftIcon={<Icon />}
                    flex={1}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>

              <Stack spacing={3}>
                <Stack direction={{ base: "column", sm: "row" }} spacing={3}>
                  <Box flex={1}>
                    <Text fontWeight={600} mb={1}>
                      Name
                    </Text>
                    <Input
                      placeholder="How should we address you?"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight={600} mb={1}>
                      Email (optional)
                    </Text>
                    <Input
                      type="email"
                      placeholder="so we can follow up"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Box>
                </Stack>
                <Box>
                  <Text fontWeight={600} mb={1}>
                    Feedback
                  </Text>
                  <Textarea
                    minH="140px"
                    placeholder="Give us the details, wish-list items, or screenshots/links."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    bg={surface}
                    borderColor={border}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Minimum 8 characters so we can act on it.
                  </Text>
                </Box>
              </Stack>

              <Divider />

              <Stack direction={{ base: "column", sm: "row" }} justify="space-between" align="center">
                <VStack align="start" spacing={0}>
                  <Text fontWeight={600}>Want a reply?</Text>
                  <Text fontSize="sm" color="gray.600">
                    Leave your email and we&apos;ll get back to you.
                  </Text>
                </VStack>
                <Button
                  colorScheme="blue"
                  rightIcon={<FaPaperPlane />}
                  type="submit"
                  isDisabled={!canSubmit}
                  isLoading={submitting}
                >
                  Send
                </Button>
              </Stack>
            </Stack>
          </DrawerBody>

          <DrawerFooter justifyContent="flex-start" px={6}>
            <Text fontSize="xs" color="gray.500">
              Your notes go straight to the product team.
            </Text>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
